import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { useLang } from "@/lib/i18n";
import Sparks from "@/components/Sparks";

/**
 * Mandatory language selection screen. Rendered after the Landing hero's
 * "Start Free Reading" CTA. Persists language choice in localStorage +
 * document.documentElement.lang; every downstream screen and every backend
 * call reads from useLang().
 */
export default function LanguageSelect() {
  const navigate = useNavigate();
  const { lang, setLang, t } = useLang();
  // On first entry, preselect current stored lang so returning users see
  // their previous choice already highlighted.
  const [choice, setChoice] = useState(lang || "en");

  useEffect(() => {
    setChoice(lang || "en");
  }, [lang]);

  const confirm = (next) => {
    setLang(next);
    // Small paint delay so the button "selected" state renders before nav.
    setTimeout(() => navigate("/onboarding"), 180);
  };

  const options = [
    {
      code: "en",
      label: t("lang.en.label"),
      note: t("lang.en.note"),
      preview: "\"Life's biggest uncertainties and questions deserve more than predictions.\"",
      previewClass: "font-serif italic",
    },
    {
      code: "hi",
      label: t("lang.hi.label"),
      note: t("lang.hi.note"),
      // Hindi hero line — same in both cards so the user can *feel* the tone
      // shift immediately.
      preview: "\"जीवन की सबसे बड़ी उलझनों और सवालों के लिए केवल भविष्यवाणी काफी नहीं होती।\"",
      previewClass: "font-serif",
    },
  ];

  return (
    <div className="min-h-screen cosmic-bg text-slate-100 flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <Sparks />

      {/* Subtle constellation */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(212,175,55,0.10), transparent 40%), radial-gradient(circle at 75% 80%, rgba(49,46,129,0.28), transparent 45%)",
        }}
      />

      <div className="relative z-10 max-w-4xl w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 mb-6">
            <Sparkles className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.35em] uppercase gold-text">
              {t("common.language")}
            </span>
          </div>
          <h1
            className="font-serif text-4xl md:text-5xl font-light text-white leading-tight"
            data-testid="lang-title"
          >
            {t("lang.title")}
          </h1>
          <p
            className="mt-6 text-slate-300 font-light max-w-2xl mx-auto leading-relaxed"
            data-testid="lang-subtitle"
          >
            {t("lang.subtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {options.map((opt, i) => {
            const selected = choice === opt.code;
            return (
              <motion.button
                key={opt.code}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.15 + i * 0.08 }}
                whileHover={{ y: -3 }}
                onClick={() => setChoice(opt.code)}
                onDoubleClick={() => confirm(opt.code)}
                className={`glass rounded-3xl p-8 md:p-10 text-left transition-all ${
                  selected
                    ? "gold-glow border-[#D4AF37]/60"
                    : "border-white/10 hover:border-[#D4AF37]/30"
                }`}
                style={
                  selected
                    ? {
                        background:
                          "linear-gradient(180deg, rgba(212,175,55,0.12), rgba(49,46,129,0.18))",
                        border: "1px solid rgba(212,175,55,0.5)",
                      }
                    : undefined
                }
                data-testid={`lang-option-${opt.code}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase gold-text mb-2">
                      {opt.code === "en" ? "01" : "02"}
                    </div>
                    <div className="font-serif text-4xl md:text-5xl font-light text-white leading-none">
                      {opt.label}
                    </div>
                    <div className="mt-3 text-xs tracking-wide text-slate-400">
                      {opt.note}
                    </div>
                  </div>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center transition ${
                      selected ? "bg-[#D4AF37]" : "border border-white/20"
                    }`}
                  >
                    {selected && (
                      <Check
                        className="w-4 h-4"
                        style={{ color: "#0B1020" }}
                        strokeWidth={3}
                      />
                    )}
                  </div>
                </div>

                <div
                  className={`mt-6 pt-6 border-t border-white/10 text-lg md:text-xl text-white/90 leading-relaxed ${opt.previewClass}`}
                  lang={opt.code}
                >
                  {opt.preview}
                </div>
              </motion.button>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={() => confirm(choice)}
            className="cta-gold inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-medium"
            data-testid="lang-continue-btn"
          >
            {t("lang.continue")}
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </motion.div>

        <div className="mt-6 text-center text-[11px] tracking-wide text-slate-500">
          {t("lang.change.later")}
        </div>
      </div>
    </div>
  );
}
